import { Components, lighten, Theme } from '@mui/material/styles';
import { ColorPalette } from './theme';

export const DEFAULT_FONT = 'Montserrat';

export const componentsOverride = (palette: ColorPalette, font?: string): Components<Omit<Theme, 'components'>> => {
  const fontFamily = `${font || DEFAULT_FONT}, Arial, sans-serif !important`;

  return {
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': {
          fontFamily,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: lighten(palette.primary.main, 0.92),
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundColor: '#fafafa',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '1rem',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          marginBottom: '4px',
          borderRadius: '4px',
          '&.Mui-selected': {
            backgroundColor: lighten(palette.primary.main, 0.8),
            borderColor: palette.primary.main,
          },
          '&:hover': {
            borderColor: lighten(palette.primary.main, 0.3),
            backgroundColor: lighten(palette.primary.main, 0.7),
          },
        },
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: {
          border: '1px solid #E0E0E0',
          borderRadius: '8px',
          padding: '15px',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained' as const,
        color: 'primary' as const,
      },
      styleOverrides: {
        root: {
          color: palette.primary.contrastText,
          borderRadius: '12px',
          px: 3,
          paddingTop: '2px',
          paddingBottom: '2px',
          textTransform: 'none',
          justifyContent: 'space-between',
          minWidth: 'auto',
          minHeight: '40px',
          '&.MuiButton-containedPrimary': {
            background: `linear-gradient(95.35deg, ${palette.primary.main} 11.81%, ${palette.primary.light} 84.7%)`,
          },
          '&.MuiButton-containedInfo': {
            background: `linear-gradient(95.35deg, ${palette.info.main} 11.81%, ${palette.info.light} 84.7%)`,
          },
          '&.MuiButton-outlinedPrimary': {
            color: `${palette.primary.main}`,
          },
          '&.MuiButton-outlinedSecondary': {
            color: `${palette.secondary.main}`,
          },
          '&.MuiButton-outlinedSuccess': {
            color: `${palette.success.main}`,
          },
          '&.MuiButton-outlinedWarning': {
            color: `${palette.warning.main}`,
          },
          '&.MuiButton-outlinedInfo': {
            color: `${palette.info.main}`,
          },
          '&.MuiButton-outlinedError': {
            color: `${palette.primary.main}`,
          },
          '&.MuiButton-outlinedInherit': {
            color: palette.grey[600],
            borderColor: palette.grey[300],
            '&:hover': {
              borderColor: palette.grey[400],
              backgroundColor: palette.grey[100],
            },
          },
          '&.Mui-disabled': {
            color: palette.primary.contrastText,
            background: `${palette.grey[400]} !important`,
          },
          '&.MuiButton-sizeLarge': {
            height: '50px',
          },
        },
        endIcon: {
          marginRight: '-14px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-colorSuccess': { color: '#fff' },
          '&.MuiChip-colorError': { color: '#fff' },
          '&.MuiChip-colorWarning': { color: '#fff' },
          '&.MuiChip-colorInfo': { color: '#fff' },
          '&.MuiChip-colorPrimary': { color: '#fff' },
          '&.MuiChip-outlinedSuccess': {
            color: palette.success.main,
          },
          '&.MuiChip-outlinedError': {
            color: palette.error.main,
          },
          '&.MuiChip-outlinedWarning': {
            color: palette.warning.main,
          },
          '&.MuiChip-outlinedInfo': {
            color: palette.info.main,
          },
          '&.MuiChip-outlinedPrimary': {
            color: palette.primary.main,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          paddingTop: '2px',
          width: '100%',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.MuiOutlinedInput-root': {
            borderRadius: '20px',
            padding: '2px 16px',
          },
          '&.MuiOutlinedInput-root.MuiInputBase-adornedEnd': {
            paddingRight: '9px',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          '&.MuiOutlinedInput-root': {
            padding: '11px 22px !important',
          },
        },
        root: {
          '& .MuiFormLabel-root': {
            padding: '6px 9px',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: palette.primary.main,
          color: palette.common.white,
          marginBottom: '12px',
          fontSize: '1.2rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 24,
        },
        paperFullScreen: {
          borderRadius: 0,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '& .MuiSlider-thumb': {
            background: '#fff',
            boxShadow: '0px 5px 8px 0px rgba(0, 0, 0, 0.25)',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h2: ({ theme }) => ({
          fontSize: '36px',
          fontWeight: '700',
          color: palette.grey[900],
          [theme.breakpoints.down('md')]: {
            fontSize: '30px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '22px',
          },
        }),
        h3: ({ theme }) => ({
          color: palette.grey[800],
          [theme.breakpoints.down('md')]: {
            fontSize: '28px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '20px',
          },
        }),
        h4: ({ theme }) => ({
          color: palette.grey[800],
          [theme.breakpoints.down('md')]: {
            fontSize: '26px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '18px',
          },
        }),
        h5: {
          color: palette.grey[800],
        },
        h6: ({ theme }) => ({
          fontSize: '20px',
          color: palette.grey[800],
          [theme.breakpoints.down('md')]: {
            fontSize: '18px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '16px',
          },
        }),
        body1: ({ theme }) => ({
          fontSize: '18px',
          fontWeight: '600',
          [theme.breakpoints.down('md')]: {
            fontSize: '16px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '14px',
          },
        }),
        body2: ({ theme }) => ({
          fontSize: '15px',
          fontWeight: '400',
          [theme.breakpoints.down('md')]: {
            fontSize: '14px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '12px',
          },
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid #dcdcdc',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '10px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: `4px 4px 0 0`,
          textTransform: 'none',
          '&.Mui-selected': {
            backgroundColor: lighten(theme.palette.primary.main, 0.9),
          },
        }),
      },
    },
  };
};
